import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Clock } from 'lucide-react';
import { ctaLabel } from '../data/products';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { StockBadge } from '../components/StockBadge';
export function Compare() {
  const {
    addToCart
  } = useCart();
  const {
    products
  } = useStore();
  const getProductBySlug = (slug: string) => products.find(p => p.slug === slug);
  const slugs = ['yarbo-lawn-mower', 'yarbo-lawn-mower-pro', 'yarbo-snow-blower', 'yarbo-blower', 'yarbo-lawn-mower-pro-snow-blower-blower'];
  const compareProducts = slugs.map(slug => getProductBySlug(slug)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  const compareData: Record<string, {
    bestFor: string;
    yardTask: string;
    includesCore: string;
    moduleType: string;
  }> = {
    'yarbo-lawn-mower': {
      bestFor: 'Standard mowing',
      yardTask: 'Lawn care',
      includesCore: 'Yes',
      moduleType: 'Lawn Mower'
    },
    'yarbo-lawn-mower-pro': {
      bestFor: 'Heavy/steep mowing',
      yardTask: 'Lawn care (Pro)',
      includesCore: 'Yes',
      moduleType: 'Lawn Mower Pro'
    },
    'yarbo-snow-blower': {
      bestFor: 'Winter snow clearing',
      yardTask: 'Snow removal',
      includesCore: 'Yes',
      moduleType: 'Snow Blower'
    },
    'yarbo-blower': {
      bestFor: 'Leaf & debris clearing',
      yardTask: 'Leaf cleanup',
      includesCore: 'Yes',
      moduleType: 'Blower'
    },
    'yarbo-lawn-mower-pro-snow-blower-blower': {
      bestFor: 'Year-round automation',
      yardTask: 'All-season',
      includesCore: 'Yes',
      moduleType: 'Pro + Snow + Blower'
    }
  };
  const rows = [{
    label: 'Best for',
    values: compareProducts.map(p => compareData[p.slug]?.bestFor || '-')
  }, {
    label: 'Yard task',
    values: compareProducts.map(p => compareData[p.slug]?.yardTask || '-')
  }, {
    label: 'Includes Yarbo Core?',
    values: compareProducts.map(p => compareData[p.slug]?.includesCore || '-')
  }, {
    label: 'Module type',
    values: compareProducts.map(p => compareData[p.slug]?.moduleType || '-')
  }];
  return <div className="bg-gray-50 min-h-screen" data-id="element-1386">
      <div className="bg-white border-b border-gray-200" data-id="element-1387">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1388">
          <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-2" data-id="element-1389">
            Compare Yarbo Systems
          </h1>
          <p className="text-gray-600 max-w-2xl" data-id="element-1390">
            Side-by-side comparison of Yarbo complete systems and the all-season
            bundle.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-id="element-1391">
        {/* Desktop table */}
        <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-1392">
          <table className="w-full" data-id="element-1393">
            <thead data-id="element-1394">
              <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1395">
                <th className="p-5 text-left text-xs font-bold uppercase tracking-wider text-gray-700 w-1/6" data-id="element-1396">
                  Feature
                </th>
                {compareProducts.map(p => {
                const isHighlighted = p.slug === 'yarbo-lawn-mower-pro-snow-blower-blower';
                return <th key={p.id} className={`p-5 text-center ${isHighlighted ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-1397">
                      <div className="aspect-square w-24 mx-auto mb-3 bg-gray-50 rounded-lg p-2" data-id="element-1398">
                        <img src={p.image} alt={p.name} className="w-full h-full object-contain" data-id="element-1399" />
                      </div>
                      <div className="text-sm font-bold text-yarbo-dark mb-1" data-id="element-1400">
                        {p.name}
                      </div>
                      <div className="text-lg font-extrabold text-yarbo-dark mb-2" data-id="element-1401">
                        ${p.price.toLocaleString()}
                      </div>
                      <StockBadge status={p.status} data-id="element-1402" />
                    </th>;
              })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" data-id="element-1403">
              {rows.map((row, idx) => <tr key={idx} data-id="element-1404">
                  <td className="p-5 font-bold text-gray-700 text-sm" data-id="element-1405">
                    {row.label}
                  </td>
                  {row.values.map((val, i) => {
                const isHighlighted = compareProducts[i]?.slug === 'yarbo-lawn-mower-pro-snow-blower-blower';
                return <td key={i} className={`p-5 text-center text-sm ${isHighlighted ? 'bg-yarbo-yellow/5 font-medium' : 'text-gray-600'}`} data-id="element-1406">
                        {val}
                      </td>;
              })}
                </tr>)}
              <tr className="bg-gray-50" data-id="element-1407">
                <td className="p-5" data-id="element-1408"></td>
                {compareProducts.map(p => {
                const isHighlighted = p.slug === 'yarbo-lawn-mower-pro-snow-blower-blower';
                return <td key={p.id} className={`p-5 text-center ${isHighlighted ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-1409">
                      <button onClick={() => addToCart(p)} className="w-full bg-yarbo-dark text-white font-bold text-sm py-3 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-1410">
                        {p.status === 'in-stock' ? <ShoppingCart className="w-4 h-4" data-id="element-1411" /> : <Clock className="w-4 h-4" data-id="element-1412" />}
                        {ctaLabel(p.status)}
                      </button>
                      <Link to={`/product/${p.slug}`} className="block mt-2 text-xs font-bold text-gray-600 hover:text-yarbo-dark" data-id="element-1413">
                        View Details →
                      </Link>
                    </td>;
              })}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile stacked */}
        <div className="lg:hidden space-y-4" data-id="element-1414">
          {compareProducts.map((p, i) => {
          const isHighlighted = p.slug === 'yarbo-lawn-mower-pro-snow-blower-blower';
          return <motion.div key={p.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className={`bg-white rounded-xl border p-5 ${isHighlighted ? 'border-yarbo-yellow bg-yarbo-yellow/5' : 'border-gray-200'}`} data-id="element-1415">
                <div className="flex gap-4 mb-4" data-id="element-1416">
                  <div className="w-20 h-20 bg-gray-50 rounded-lg p-2 flex-shrink-0" data-id="element-1417">
                    <img src={p.image} alt={p.name} className="w-full h-full object-contain" data-id="element-1418" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1419">
                    <h3 className="font-bold text-yarbo-dark mb-1" data-id="element-1420">{p.name}</h3>
                    <div className="text-xl font-extrabold text-yarbo-dark mb-2" data-id="element-1421">
                      ${p.price.toLocaleString()}
                    </div>
                    <StockBadge status={p.status} data-id="element-1422" />
                  </div>
                </div>
                <dl className="space-y-2 text-sm mb-4" data-id="element-1423">
                  {rows.map((row, idx) => <div key={idx} className="flex justify-between gap-3" data-id="element-1424">
                      <dt className="text-gray-500" data-id="element-1425">{row.label}</dt>
                      <dd className="text-gray-800 font-medium text-right" data-id="element-1426">
                        {row.values[i]}
                      </dd>
                    </div>)}
                </dl>
                <div className="flex gap-2" data-id="element-1427">
                  <button onClick={() => addToCart(p)} className="flex-1 bg-yarbo-dark text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2" data-id="element-1428">
                    {p.status === 'in-stock' ? <ShoppingCart className="w-4 h-4" data-id="element-1429" /> : <Clock className="w-4 h-4" data-id="element-1430" />}
                    {ctaLabel(p.status)}
                  </button>
                  <Link to={`/product/${p.slug}`} className="flex-1 text-center text-yarbo-dark border border-gray-200 font-bold py-3 rounded-lg text-sm" data-id="element-1431">
                    View Details
                  </Link>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </div>;
}